import type { JSX } from 'react';
import { useCallback, useEffect, useRef, useState } from 'react';
import { flip, FloatingPortal, offset, shift, size } from '@floating-ui/react';
import { PluginKey } from '@tiptap/pm/state';

// --- Hooks ---
import type { Range } from '@tiptap/react';

import { Suggestion } from '@tiptap/suggestion';

import { SuggestionPluginKey, type SuggestionKeyDownProps, type SuggestionProps } from '@tiptap/suggestion';

import { useFloatingElement } from 'containers/Tiptap/hooks/use-floating-element';
import { useMenuNavigation } from 'containers/Tiptap/hooks/use-menu-navigation';
import { useTiptapEditor } from 'containers/Tiptap/hooks/use-tiptap-editor';

// --- Tiptap Editor ---

// --- Tiptap UI ---

// --- UI Primitives ---

import { calculateStartPosition } from 'containers/Tiptap/components/tiptap-ui-utils/suggestion-menu/suggestion-menu-utils';
import {
  consumeProgrammaticSlash,
  updateInlineSlashEnd,
} from 'containers/Tiptap/components/tiptap-ui-utils/suggestion-menu/programmatic-slash';
import type {
  SuggestionItem,
  SuggestionMenuProps,
} from 'containers/Tiptap/components/tiptap-ui-utils/suggestion-menu/suggestion-menu-types';

/**
 * A component that renders a suggestion menu for Tiptap editors.
 * Displays a floating menu when a trigger character is typed.
 */
export const SuggestionMenu = ({
  editor: providedEditor,
  floatingOptions,
  selector = 'tiptap-suggestion-menu',
  children,
  maxHeight = 384,
  pluginKey = SuggestionPluginKey,
  ...internalSuggestionProps
}: SuggestionMenuProps): JSX.Element | null => {
  const { editor } = useTiptapEditor(providedEditor);

  const [show, setShow] = useState<boolean>(false);

  // If later we want the floating stick to the position while browser is scrolling,
  // we can uncomment this part and pass the getBoundingClientRect prop to FloatingElement instead of referenceElement.
  // const [internalClientRect, setInternalClientRect] = useState<DOMRect | null>(
  //   null
  // )
  const [internalDecorationNode, setInternalDecorationNode] = useState<HTMLElement | null>(null);
  const [internalCommand, setInternalCommand] = useState<((item: SuggestionItem) => void) | null>(null);
  const [internalItems, setInternalItems] = useState<SuggestionItem[]>([]);
  const [internalQuery, setInternalQuery] = useState<string>('');
  const [, setInternalRange] = useState<Range | null>(null);

  // Remove the '/' inserted by the drag-handle plus button if the user dismissed
  // the menu without selecting an item. No-op for user-typed '/'. Two cases:
  //   - 'paragraph' kind: a whole new paragraph wraps the '/' (non-textblock fallback)
  //     — verify node identity (still a paragraph starting with '/') before deleting.
  //   - 'inline' kind: '/' was inserted into an existing textblock; range.to has been
  //     extended via onUpdate to cover any chars typed into the suggestion query.
  //     Verify the range still starts with '/' before deleting.
  // Identity checks guard against collab (Y.js) edits shifting the doc out from under us.
  const cleanupProgrammaticSlash = useCallback((): void => {
    if (!editor || editor.isDestroyed) return;
    const cleanup = consumeProgrammaticSlash(editor);
    if (!cleanup) return;
    try {
      const { doc } = editor.state;
      if (cleanup.from < 0 || cleanup.from > doc.content.size) return;

      if (cleanup.kind === 'paragraph') {
        const node = doc.resolve(cleanup.from).nodeAfter;
        if (!node || node.type.name !== 'paragraph') return;
        if (!node.textContent.startsWith('/')) return;
        editor.chain().deleteRange({ from: cleanup.from, to: cleanup.from + node.nodeSize }).run();
        return;
      }

      // inline: clamp to current doc size in case the editor shrank concurrently.
      const to = Math.min(cleanup.to, doc.content.size);
      if (to <= cleanup.from) return;
      if (doc.textBetween(cleanup.from, cleanup.from + 1) !== '/') return;
      editor.chain().deleteRange({ from: cleanup.from, to }).run();
    } catch {
      // position may be stale if the doc was mutated concurrently; safe to ignore
    }
  }, [editor]);

  const { ref, style, getFloatingProps, isMounted } = useFloatingElement(show, internalDecorationNode, 1000, {
    placement: 'bottom-start',
    middleware: [
      offset(10),
      flip({
        mainAxis: true,
        crossAxis: false,
      }),
      shift(),
      size({
        apply({ availableHeight, elements }): void {
          if (elements.floating) {
            const maxHeightValue = maxHeight ? Math.min(maxHeight, availableHeight) : availableHeight;

            elements.floating.style.setProperty('--suggestion-menu-max-height', `${maxHeightValue}px`);
          }
        },
      }),
    ],
    onOpenChange(open): void {
      if (!open) {
        setShow(false);
        cleanupProgrammaticSlash();
      }
    },
    dismissOptions: {
      // Don't treat a click on the drag-handle plus button as an outside press:
      // otherwise the popup is dismissed (and the programmatic '/' cleaned up)
      // before handleAddBlock runs, which defeats the spam guard and makes the
      // '/' appear to travel line-by-line on repeated clicks.
      outsidePress: (event): boolean => {
        const target = event.target as Element | null;
        return !target?.closest('.drag-handle-add');
      },
    },
    ...floatingOptions,
  });

  const internalSuggestionPropsRef = useRef(internalSuggestionProps);

  useEffect(() => {
    internalSuggestionPropsRef.current = internalSuggestionProps;
  }, [internalSuggestionProps]);

  const closePopup = useCallback((): void => {
    setShow(false);
  }, []);

  useEffect(() => {
    if (!editor || editor.isDestroyed) {
      return undefined;
    }

    const existingPlugin = editor.state.plugins.find((plugin) => plugin.spec.key === pluginKey);
    if (existingPlugin) {
      editor.unregisterPlugin(pluginKey);
    }

    const suggestion = Suggestion({
      pluginKey: pluginKey instanceof PluginKey ? pluginKey : new PluginKey(pluginKey),
      editor,

      allow(props): boolean {
        // Use props.state.doc, not editor.state.doc: inside Plugin.apply the editor
        // has not yet swapped state, so editor.state.doc is stale and a new-state
        // range can be out of range (VPR-6127: "Position N out of range").
        const { doc } = props.state;
        const from = Math.min(Math.max(props.range.from, 0), doc.content.size);
        const $from = doc.resolve(from);

        // Don't allow slash command inside an image node (captions are supported).
        for (let { depth } = $from; depth > 0; depth--) {
          if ($from.node(depth).type.name === 'image') {
            return false;
          }
        }

        return true;
      },

      command({ editor: cmdEditor, range, props }): void {
        consumeProgrammaticSlash(cmdEditor);
        if (!range) {
          return;
        }

        const { view, state } = cmdEditor;
        const { selection } = state;

        const cursorPosition = selection.$from.pos;
        const previousNode = selection.$head?.nodeBefore;

        const startPosition = previousNode
          ? calculateStartPosition(cursorPosition, previousNode, internalSuggestionPropsRef.current.char)
          : selection.$from.start();

        const transaction = state.tr.deleteRange(startPosition, cursorPosition);
        view.dispatch(transaction);

        const { nodeAfter } = view.state.selection.$to;
        const overrideSpace = nodeAfter?.text?.startsWith(' ');

        const rangeToUse = { ...range };

        if (overrideSpace) {
          rangeToUse.to += 1;
        }

        props.onSelect({ editor: cmdEditor, range: rangeToUse, context: props.context });
      },

      render: () => ({
        onStart: (props: SuggestionProps<SuggestionItem>): void => {
          setInternalDecorationNode((props.decorationNode as HTMLElement) ?? null);
          setInternalCommand(() => props.command);
          setInternalItems(props.items);
          setInternalQuery(props.query);
          setInternalRange(props.range);
          // setInternalClientRect(props.clientRect?.() ?? null)
          setShow(true);
        },

        onUpdate: (props: SuggestionProps<SuggestionItem>): void => {
          setInternalDecorationNode((props.decorationNode as HTMLElement) ?? null);
          setInternalCommand(() => props.command);
          setInternalItems(props.items);
          setInternalQuery(props.query);
          setInternalRange(props.range);
          // setInternalClientRect(props.clientRect?.() ?? null)
          // Extend inline cleanup range to cover any chars the user types as the suggestion query.
          updateInlineSlashEnd(editor, props.range.to);
        },

        onKeyDown: (props: SuggestionKeyDownProps): boolean => {
          if (props.event.key === 'Escape') {
            closePopup();
            return true;
          }
          return false;
        },

        onExit: (): void => {
          cleanupProgrammaticSlash();
          setInternalDecorationNode(null);
          setInternalCommand(null);
          setInternalItems([]);
          setInternalQuery('');
          setInternalRange(null);
          // setInternalClientRect(null)
          setShow(false);
        },
      }),
      ...internalSuggestionPropsRef.current,
    });

    editor.registerPlugin(suggestion);

    return () => {
      try {
        if (!editor.isDestroyed && editor.view && !editor.view.isDestroyed) {
          editor.unregisterPlugin(pluginKey);
        }
      } catch (error) {
        // Silently ignore cleanup errors during editor destruction
        console.debug('SuggestionMenu cleanup error:', error);
      }
    };
  }, [editor, pluginKey, closePopup, cleanupProgrammaticSlash]);

  const onSelect = useCallback(
    (item: SuggestionItem): void => {
      closePopup();

      if (internalCommand) {
        internalCommand(item);
      }
    },
    [closePopup, internalCommand]
  );

  const { selectedIndex } = useMenuNavigation({
    editor,
    query: internalQuery,
    items: internalItems,
    onSelect,
  });

  if (!isMounted || !show || !editor) {
    return null;
  }

  // Portal to the editor wrapper (not body, so its theming still applies) to
  // escape the layout's `overflow-x: auto`, which was clipping the menu.
  const portalRoot = editor.view.dom.closest<HTMLElement>('.notion-like-editor-wrapper');

  return (
    <FloatingPortal root={portalRoot}>
      <div
        ref={ref}
        style={style}
        {...getFloatingProps()}
        data-selector={selector}
        className="tiptap-suggestion-menu"
        role="listbox"
        aria-label="Suggestions"
        onPointerDown={(e): void => e.preventDefault()}
      >
        {children({
          items: internalItems,
          selectedIndex,
          onSelect,
        })}
      </div>
    </FloatingPortal>
  );
};
